import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertTriangle, ArrowLeft, Info, Search } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { enumLabel, useI18n } from '../lib/i18n';
import { usePeriod } from '../lib/period';
import type { ScoreLabel } from '../lib/types';
import { useMoney, useNumber } from './kit';
import { Badge, ErrorState, Field, Input, LoadingBlock, Select, priorityTone, statusTone, type BadgeTone } from './ui';

/**
 * Pieces the People & work pages share (Employees, Points, Work, Projects & Clients, Access, Settings): the
 * filters' options, codes in words, company days and instants, filters kept in the address, notes, and the
 * loading / error frame every query gets.
 */

const TAG = { en: 'en-GB', ar: 'ar-EG-u-nu-latn' } as const;

/* ---------------------------------------------------------------------------------------------- */
/* Data every page may need                                                                        */
/* ---------------------------------------------------------------------------------------------- */

/** What the filters offer (`GET /executive/filters`). */
export interface FilterOptions {
  departments: Array<{ id: string; name: string; nameAr: string | null; code: string }>;
  employees: Array<{ id: string; fullName: string; fullNameAr: string | null; isActive: boolean; jobTitle: string | null }>;
  clients: Array<{ id: string; code: string; companyName: string; companyNameAr: string | null; status: string }>;
  projects: Array<{ id: string; code: string; name: string; nameAr: string | null; status: string }>;
}

export function useFilterOptions() {
  return useQuery({ queryKey: ['executive', 'filters'], queryFn: () => api.get<FilterOptions>('/executive/filters'), staleTime: 5 * 60_000 });
}

interface ExecutiveMe {
  company: { baseCurrency: string; timezone: string };
}

/** The company's base currency and time zone (`GET /executive/me`) — '' / undefined until they are known. */
export function useCompany(): { baseCurrency: string; timezone: string | undefined } {
  const { data } = useQuery({ queryKey: ['executive', 'me'], queryFn: () => api.get<ExecutiveMe>('/executive/me'), staleTime: 5 * 60_000 });
  return { baseCurrency: data?.company?.baseCurrency ?? '', timezone: data?.company?.timezone || undefined };
}

/* ---------------------------------------------------------------------------------------------- */
/* Words, numbers, dates                                                                           */
/* ---------------------------------------------------------------------------------------------- */

/** A code the API sends, in words: the dictionary's when it has one, else the enum's own label. */
export function useCode() {
  const { t, has, locale } = useI18n();
  return (prefix: string, code: string | null | undefined, vars?: Record<string, string | number>) => {
    if (!code) return '—';
    const key = `${prefix}.${code}`;
    return has(key) ? t(key, vars) : enumLabel(code, locale);
  };
}

/** A percentage, or "N/A" when there was nothing to divide. */
export function usePercent() {
  const num = useNumber();
  const { t } = useI18n();
  return (value: number | null | undefined, digits = 1) => (value === null || value === undefined || !Number.isFinite(value) ? t('work.na') : `${num(value, digits)}%`);
}

/** An amount in its currency; a bare number when the currency is not known yet; "Unknown" for null. */
export function useAmount() {
  const money = useMoney();
  const num = useNumber();
  const { t } = useI18n();
  return (value: number | null | undefined, currency: string | null | undefined) => {
    if (value === null || value === undefined || !Number.isFinite(value)) return t('common.unknown');
    return currency ? money(value, currency) : num(value, 2);
  };
}

/** Hours as hours under two days, as days beyond. */
export function useHours() {
  const num = useNumber();
  const { t } = useI18n();
  return (hours: number | null | undefined) => {
    if (hours === null || hours === undefined || !Number.isFinite(hours)) return '—';
    return hours < 48 ? t('work.hours', { n: num(hours, 1) }) : t('work.days', { n: num(hours / 24, 1) });
  };
}

/**
 * Company days (`YYYY-MM-DD`) as written — never moved a day by the browser's time zone — and instants on the
 * company's clock when its zone is known.
 */
export function useDates() {
  const { locale } = useI18n();
  return useMemo(() => {
    const tag = TAG[locale];
    const cache = new Map<string, Intl.DateTimeFormat>();
    const fmt = (key: string, options: Intl.DateTimeFormatOptions) => {
      let f = cache.get(key);
      if (!f) {
        try {
          f = new Intl.DateTimeFormat(tag, options);
        } catch {
          // An unknown zone name: the browser's own clock rather than nothing.
          const { timeZone: _z, ...rest } = options;
          f = new Intl.DateTimeFormat(tag, rest);
        }
        cache.set(key, f);
      }
      return f;
    };
    const asDay = (value: string) => new Date(`${value.slice(0, 10)}T00:00:00Z`);
    const valid = (d: Date) => !Number.isNaN(d.getTime());
    return {
      day(value: string | null | undefined): string {
        if (!value) return '—';
        const d = asDay(value);
        return valid(d) ? fmt('day', { dateStyle: 'medium', timeZone: 'UTC' }).format(d) : '—';
      },
      shortDay(value: string): string {
        const d = asDay(value);
        return valid(d) ? fmt('short', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(d) : value;
      },
      month(value: string | null | undefined): string {
        if (!value) return '—';
        const d = new Date(`${value.slice(0, 7)}-01T00:00:00Z`);
        return valid(d) ? fmt('month', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(d) : value;
      },
      shortMonth(value: string): string {
        const d = new Date(`${value.slice(0, 7)}-01T00:00:00Z`);
        return valid(d) ? fmt('shortMonth', { month: 'short', year: '2-digit', timeZone: 'UTC' }).format(d) : value;
      },
      at(value: string | Date | null | undefined, timeZone?: string): string {
        if (!value) return '—';
        const d = typeof value === 'string' ? new Date(value) : value;
        if (!valid(d)) return '—';
        return fmt(`at:${timeZone ?? ''}`, { dateStyle: 'medium', timeStyle: 'short', ...(timeZone ? { timeZone } : {}) }).format(d);
      },
    };
  }, [locale]);
}

/* ---------------------------------------------------------------------------------------------- */
/* Small pieces                                                                                    */
/* ---------------------------------------------------------------------------------------------- */

/** The score labels' colours — the same as the score badge's. */
export const SCORE_LABEL_TONE: Record<ScoreLabel, BadgeTone> = { EXCELLENT: 'success', VERY_GOOD: 'info', GOOD: 'neutral', NEEDS_ATTENTION: 'warning' };
export const SCORE_LABELS: ScoreLabel[] = ['EXCELLENT', 'VERY_GOOD', 'GOOD', 'NEEDS_ATTENTION'];

/** A record's status as a badge, in words. */
export function StatusBadge({ status }: { status: string | null | undefined }) {
  const { locale } = useI18n();
  if (!status) return <span className="text-muted">—</span>;
  return <Badge tone={statusTone(status)}>{enumLabel(status, locale)}</Badge>;
}

/** A status in this dashboard's own words for it (the wallet's, say), coloured as every status is. */
export function CodeBadge({ prefix, value }: { prefix: string; value: string | null | undefined }) {
  const code = useCode();
  if (!value) return <span className="text-muted">—</span>;
  return <Badge tone={statusTone(value)}>{code(prefix, value)}</Badge>;
}

export function PriorityBadge({ priority }: { priority: string | null | undefined }) {
  const { locale } = useI18n();
  if (!priority) return <span className="text-muted">—</span>;
  return <Badge tone={priorityTone(priority)}>{enumLabel(priority, locale)}</Badge>;
}

/** A short line that explains a figure, or warns about it. */
export function Note({ children, tone = 'info', className }: { children: ReactNode; tone?: 'info' | 'warning'; className?: string }) {
  const Icon = tone === 'warning' ? AlertTriangle : Info;
  return (
    <p className={clsx('flex items-start gap-2 text-caption text-muted', className)}>
      <Icon className={clsx('mt-0.5 h-3.5 w-3.5 shrink-0', tone === 'warning' ? 'text-warning-fg dark:text-warning-dark' : 'text-[var(--text-tertiary)]')} aria-hidden />
      <span>{children}</span>
    </p>
  );
}

/** Some evidence was past the read limit. */
export function TruncatedNote({ truncated }: { truncated: boolean | null | undefined }) {
  const { t } = useI18n();
  if (!truncated) return null;
  return <Note tone="warning">{t('state.truncated')}</Note>;
}

export function BackLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="mb-3 inline-flex items-center gap-1.5 text-body-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
      <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
      {children}
    </Link>
  );
}

const LINK = 'text-[var(--text-primary)] underline-offset-4 hover:text-primary-600 hover:underline dark:hover:text-primary-400';

/** A link in a table or a list, in the reading colour. */
export function TextLink({ to, children, className }: { to: string; children: ReactNode; className?: string }) {
  return (
    <Link to={to} className={clsx(LINK, className)}>
      {children}
    </Link>
  );
}

/** A person, linking to their performance page (and a tab of it), the period kept. */
export function PersonLink({ person, query, tab }: { person: { id: string; fullName: string; fullNameAr: string | null }; query: string; tab?: string }) {
  const { localized } = useI18n();
  const search = new URLSearchParams(query);
  if (tab) search.set('tab', tab);
  return (
    <Link to={`/employees/${person.id}?${search.toString()}`} className={clsx(LINK, 'font-medium')}>
      {localized(person)}
    </Link>
  );
}

/** A person's name, or a dash. */
export function PersonName({ person }: { person: { fullName: string; fullNameAr: string | null } | null | undefined }) {
  const { localized } = useI18n();
  return person ? <span>{localized(person)}</span> : <span className="text-muted">—</span>;
}

/** The read-only page of one record behind the figures. */
export function sourceHref(sourceType: string, id: string, query: string): string | null {
  const type = sourceType === 'TASK' ? 'task' : sourceType === 'TICKET' ? 'ticket' : sourceType === 'Q_TASK' ? 'qtask' : null;
  return type ? `/work/evidence/${type}/${id}${query ? `?${query}` : ''}` : null;
}

/**
 * The record an evidence id names (`task:…`, `step:…`, `qtask:…`, `review_waiting:…`, `view:<ticket>:<user>`).
 * Null where the id names a moment (a review, a resolution) rather than its record, or could be either kind.
 */
export function evidenceIdHref(evidenceId: string, query: string): string | null {
  const [prefix, first] = evidenceId.split(':');
  if (!first) return null;
  if (prefix === 'task' || prefix === 'step' || prefix === 'review_waiting') return sourceHref('TASK', first, query);
  if (prefix === 'qtask') return sourceHref('Q_TASK', first, query);
  if (prefix === 'view') return sourceHref('TICKET', first, query);
  return null;
}

/** A label above a value, in a grid of them. */
export function Stat({ label, value, hint, tone = 'neutral' }: { label: ReactNode; value: ReactNode; hint?: ReactNode; tone?: 'neutral' | 'warning' | 'error' | 'success' }) {
  return (
    <div className="min-w-0 rounded-md bg-[var(--surface-sunken)] px-3 py-2.5">
      <dt className="text-caption text-[var(--text-tertiary)]">{label}</dt>
      <dd
        className={clsx(
          'mt-0.5 text-title tabular',
          tone === 'neutral' && 'text-[var(--text-primary)]',
          tone === 'warning' && 'text-warning-fg dark:text-warning-dark',
          tone === 'error' && 'text-error-fg dark:text-error-dark',
          tone === 'success' && 'text-success-fg dark:text-success',
        )}
      >
        {value}
      </dd>
      {hint && <dd className="mt-0.5 text-caption text-muted">{hint}</dd>}
    </div>
  );
}

/** A grid of stats; `columns` replaces the default columns (for a card that sits half-width). */
export function StatGrid({ children, columns }: { children: ReactNode; columns?: string }) {
  return <dl className={clsx('grid gap-2', columns ?? 'grid-cols-2 sm:grid-cols-3 xl:grid-cols-4')}>{children}</dl>;
}

/* ---------------------------------------------------------------------------------------------- */
/* Filters kept in the address                                                                     */
/* ---------------------------------------------------------------------------------------------- */

/** A search box written to the address once typing pauses — a reload or a link keeps it. */
export function SearchBox({ param = 'q', label, placeholder }: { param?: string; label: string; placeholder?: string }) {
  const { param: read, setParam } = usePeriod();
  const current = read(param);
  const [text, setText] = useState(current);
  // The address moved (back button, a link): follow it, unless it is what is being typed.
  useEffect(() => setText((typed) => (typed.trim() === current ? typed : current)), [current]);
  useEffect(() => {
    if (text.trim() === current) return;
    const timer = setTimeout(() => setParam(param, text.trim() || null), 350);
    return () => clearTimeout(timer);
  }, [text, current, param, setParam]);
  return (
    <Field label={label} className="min-w-[12rem] flex-1">
      <span className="relative block">
        <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-tertiary)]" aria-hidden />
        <Input type="search" value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} className="ps-9" />
      </span>
    </Field>
  );
}

/** A select written to the address. */
export function ParamSelect({
  param,
  label,
  allLabel,
  options,
  className,
}: {
  param: string;
  label: string;
  allLabel: string;
  options: Array<{ value: string; label: string }>;
  className?: string;
}) {
  const { param: read, setParam } = usePeriod();
  const value = read(param);
  // A value in the address the options do not hold (yet): kept, never shown as "All".
  const shown = value && !options.some((o) => o.value === value) ? [...options, { value, label: '…' }] : options;
  return (
    <Field label={label} className={clsx('min-w-[11rem] sm:w-56', className)}>
      <Select value={value} onChange={(e) => setParam(param, e.target.value || null)}>
        <option value="">{allLabel}</option>
        {shown.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
    </Field>
  );
}

/** The row of filters above everything they scope. */
export function FilterRow({ children }: { children: ReactNode }) {
  return <div className="mb-5 flex flex-wrap items-end gap-3">{children}</div>;
}

/* ---------------------------------------------------------------------------------------------- */
/* A query's frame                                                                                 */
/* ---------------------------------------------------------------------------------------------- */

/**
 * Loading, an error with retry, then the data. A refetch for a new filter holds the previous figures,
 * dimmed, instead of flashing an empty page.
 */
export function Loaded<T>({ query, children }: { query: UseQueryResult<T>; children: (data: T) => ReactNode }) {
  if (query.data === undefined) {
    if (query.error && !query.isFetching) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
    return <LoadingBlock />;
  }
  return <div className={clsx('transition-opacity duration-fast', query.isPlaceholderData && 'opacity-60')}>{children(query.data)}</div>;
}
